import React from 'react';
import { motion } from 'framer-motion';
import { format } from 'date-fns';
import jsPDF from 'jspdf';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiAward, FiDownload, FiRotateCcw, FiX } = FiIcons;

const StoryComplete = ({ studentName, storyTitle, isSuccess, onRestart }) => {
  const generateCertificate = () => {
    const pdf = new jsPDF();
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();

    // Background
    pdf.setFillColor(240, 248, 255);
    pdf.rect(0, 0, pageWidth, pageHeight, 'F');

    // Border
    pdf.setDrawColor(43, 103, 119);
    pdf.setLineWidth(3);
    pdf.rect(10, 10, pageWidth - 20, pageHeight - 20);

    // Title
    pdf.setFontSize(24);
    pdf.setTextColor(43, 103, 119);
    pdf.text('Abschlusszertifikat', pageWidth / 2, 40, { align: 'center' });

    // Decorative line
    pdf.setDrawColor(43, 103, 119);
    pdf.setLineWidth(1);
    pdf.line(50, 50, pageWidth - 50, 50);

    // Content
    pdf.setFontSize(16);
    pdf.setTextColor(0, 0, 0);
    pdf.text('Hiermit wird bestätigt, dass', pageWidth / 2, 80, { align: 'center' });

    pdf.setFontSize(20);
    pdf.setTextColor(43, 103, 119);
    pdf.text(studentName, pageWidth / 2, 100, { align: 'center' });

    pdf.setFontSize(16);
    pdf.setTextColor(0, 0, 0);
    pdf.text('die interaktive Geschichte erfolgreich abgeschlossen hat', pageWidth / 2, 120, { align: 'center' });

    pdf.setFontSize(18);
    pdf.setTextColor(43, 103, 119);
    pdf.text(`"${storyTitle}"`, pageWidth / 2, 140, { align: 'center' });

    pdf.setFontSize(14);
    pdf.setTextColor(0, 0, 0);
    pdf.text(`Abgeschlossen am ${format(new Date(), 'dd. MMMM yyyy')}`, pageWidth / 2, 170, { align: 'center' });

    // Footer
    pdf.setFontSize(12);
    pdf.setTextColor(100, 100, 100);
    pdf.text('Erstellt von Interaktive Entscheidungsgeschichten', pageWidth / 2, pageHeight - 30, { align: 'center' });

    // Download
    pdf.save(`${studentName}_${storyTitle}_Zertifikat.pdf`);
  };

  return (
    <motion.div
      className="max-w-2xl mx-auto text-center"
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.9 }}
      transition={{ duration: 0.6 }}
    >
      <div className="bg-white rounded-xl shadow-lg p-8">
        {isSuccess ? (
          <>
            {/* Success */}
            <motion.div
              className="inline-flex items-center justify-center w-20 h-20 rounded-full mb-6"
              style={{ backgroundColor: '#dcfce7', color: '#16a34a' }}
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
            >
              <SafeIcon icon={FiAward} className="w-10 h-10" />
            </motion.div>

            <motion.h1
              className="text-3xl font-bold mb-4 font-rubik"
              style={{ color: '#2b6777' }}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
            >
              Herzlichen Glückwunsch, {studentName}!
            </motion.h1>

            <motion.p
              className="text-lg mb-8 font-rubik"
              style={{ color: '#2b6777' }}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
            >
              Sie haben <strong>"{storyTitle}"</strong> erfolgreich abgeschlossen und Ihr Zertifikat verdient!
            </motion.p>

            <motion.div
              className="flex flex-col sm:flex-row gap-4 justify-center"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8 }}
            >
              <motion.button
                onClick={generateCertificate}
                className="flex items-center justify-center space-x-2 px-6 py-3 rounded-lg transition-colors text-white font-rubik"
                style={{ backgroundColor: '#16a34a' }}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <SafeIcon icon={FiDownload} className="w-5 h-5" />
                <span>Zertifikat herunterladen</span>
              </motion.button>

              <motion.button
                onClick={onRestart}
                className="flex items-center justify-center space-x-2 px-6 py-3 rounded-lg transition-colors text-white font-rubik"
                style={{ backgroundColor: '#6b7280' }}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <SafeIcon icon={FiRotateCcw} className="w-5 h-5" />
                <span>Nochmal versuchen</span>
              </motion.button>
            </motion.div>
          </>
        ) : (
          <>
            {/* Failure */}
            <motion.div
              className="inline-flex items-center justify-center w-20 h-20 rounded-full mb-6"
              style={{ backgroundColor: '#fee2e2', color: '#dc2626' }}
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
            >
              <SafeIcon icon={FiX} className="w-10 h-10" />
            </motion.div>

            <motion.h1
              className="text-3xl font-bold mb-4 font-rubik"
              style={{ color: '#2b6777' }}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
            >
              Spiel beendet, {studentName}
            </motion.h1>

            <motion.p
              className="text-lg mb-8 font-rubik"
              style={{ color: '#2b6777' }}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
            >
              Keine Sorge! Jede Geschichte ist eine Lernerfahrung. Versuchen Sie es nochmal und schauen Sie, ob Sie ein anderes Ende erreichen können.
            </motion.p>

            <motion.div
              className="flex justify-center"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8 }}
            >
              <motion.button
                onClick={onRestart}
                className="flex items-center justify-center space-x-2 px-6 py-3 rounded-lg transition-colors text-white font-rubik"
                style={{ backgroundColor: '#c8d8e4' }}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <SafeIcon icon={FiRotateCcw} className="w-5 h-5" />
                <span>Nochmal versuchen</span>
              </motion.button>
            </motion.div>
          </>
        )}
      </div>
    </motion.div>
  );
};

export default StoryComplete;